/**
 * システム全体コスト上限設定カード
 *
 * テナント全体（全ユーザー合計）の月額コスト上限と、当月の消化状況を表示する。
 * 本カードはテナント全体のキルスイッチであり、0 を保存すれば全ユーザーが即時ブロックされる。
 * そのため既存の「鉛筆アイコン → 入力 → Enter で即保存」は用いず、入力確定と送信を
 * 分離した 2 段階コミットとする。
 */
import type { SystemCostLimitStatus } from '@kyumo/common';
import { Globe, Loader2, Pencil, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  useCostLimits,
  useDeleteSystemCostLimit,
  useSetSystemCostLimit,
} from '../../hooks/use-admin-api';
import { formatCurrency, getCurrentMonth } from './chart-utils';

/** 逼迫と判定する使用率の閾値（%）。サーバー側の判定と揃える */
const WARNING_THRESHOLD_PERCENT = 80;

interface SystemCostLimitCardProps {
  /** ダッシュボードで選択中の月（'YYYY-MM'）。補足表示の判定にのみ用いる */
  selectedMonth: string;
}

type Stage = 'idle' | 'editing' | 'confirming-save' | 'confirming-delete';

/** 対象期間を 'YYYY-MM' へ整形 */
function formatPeriod(period: SystemCostLimitStatus['period']): string {
  return `${period.year}-${String(period.month).padStart(2, '0')}`;
}

/**
 * 消化状況が利用可能か
 *
 * 応答に含まれない場合と degraded の場合は、いずれも「取得できていない」表示へ倒す。
 * 0% / $0 として描画すると、上限が実質無効化されている状態が健全に見えてしまう。
 */
function isStatusAvailable(
  status: SystemCostLimitStatus | undefined,
): status is SystemCostLimitStatus {
  return status !== undefined && !status.degraded;
}

export function SystemCostLimitCard({ selectedMonth }: SystemCostLimitCardProps) {
  const { data: costLimits, isLoading } = useCostLimits();
  const setSystemLimit = useSetSystemCostLimit();
  const deleteSystemLimit = useDeleteSystemCostLimit();

  const [stage, setStage] = useState<Stage>('idle');
  const [editValue, setEditValue] = useState('');
  /** 直近の保存・削除が失敗した理由。null = 失敗なし */
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const systemLimit = costLimits?.systemLimit ?? null;
  const systemStatus = costLimits?.systemStatus;
  const statusAvailable = isStatusAvailable(systemStatus);

  const isMutating = setSystemLimit.isPending || deleteSystemLimit.isPending;
  const isCurrentMonth = selectedMonth === getCurrentMonth();

  /**
   * 入力値の検証結果。null = 妥当
   *
   * サーバー側は 0 以上・小数点以下 2 桁まで（multipleOf(0.01)）しか受け付けない。
   * input の step 属性は手入力を弾かないため、同じ制約をここでも検証して
   * 「送信して 400 が返るだけ」の状態を作らない。
   */
  const validationError = useMemo(() => {
    const trimmed = editValue.trim();
    if (trimmed === '') return null;
    if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(trimmed)) {
      return '0 以上・小数点以下 2 桁までの数値で入力してください。';
    }
    return null;
  }, [editValue]);

  /** 入力値が有効なら数値、そうでなければ null */
  const parsedValue = useMemo(() => {
    const trimmed = editValue.trim();
    if (trimmed === '' || validationError !== null) return null;
    const value = Number.parseFloat(trimmed);
    if (Number.isNaN(value) || value < 0) return null;
    return value;
  }, [editValue, validationError]);

  const handleEdit = useCallback(() => {
    setEditValue(systemLimit ? systemLimit.limitUSD.toString() : '');
    setErrorMessage(null);
    setStage('editing');
  }, [systemLimit]);

  const handleCancel = useCallback(() => {
    setStage('idle');
    setEditValue('');
    setErrorMessage(null);
  }, []);

  const handleProceedToConfirm = useCallback(() => {
    if (parsedValue === null) return;
    setErrorMessage(null);
    setStage('confirming-save');
  }, [parsedValue]);

  // 失敗時は確認段階に留め、理由を提示する。テナント全体のキルスイッチであり、
  // 「保存されたのかどうか分からない」まま画面を閉じさせない。
  const handleConfirmSave = useCallback(async () => {
    if (parsedValue === null) return;
    setErrorMessage(null);
    try {
      await setSystemLimit.mutateAsync(parsedValue);
    } catch (error) {
      setErrorMessage(
        `上限を保存できませんでした: ${error instanceof Error ? error.message : String(error)}`,
      );
      return;
    }
    setStage('idle');
    setEditValue('');
  }, [parsedValue, setSystemLimit]);

  const handleConfirmDelete = useCallback(async () => {
    setErrorMessage(null);
    try {
      await deleteSystemLimit.mutateAsync();
    } catch (error) {
      setErrorMessage(
        `上限を削除できませんでした: ${error instanceof Error ? error.message : String(error)}`,
      );
      return;
    }
    setStage('idle');
  }, [deleteSystemLimit]);

  // Enter は「確認へ進む」まで、Escape は全段階で取消。確認段階の確定はボタン押下のみ。
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleProceedToConfirm();
      }
      if (e.key === 'Escape') handleCancel();
    },
    [handleProceedToConfirm, handleCancel],
  );

  const usagePercent = statusAvailable ? systemStatus.usagePercent : null;
  const isExceeded = usagePercent !== null && usagePercent >= 100;
  const isNearLimit =
    usagePercent !== null && usagePercent >= WARNING_THRESHOLD_PERCENT && usagePercent < 100;

  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Globe className="w-5 h-5" />
          システム全体の月額コスト上限
        </h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* 上限値と編集操作 */}
          <div>
            <div className="text-sm text-muted-foreground mb-1">上限（全ユーザーの合計）</div>
            {stage === 'editing' ? (
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground">$</span>
                  <input
                    id="system-cost-limit-input"
                    aria-label="システム全体の月額上限（USD）"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-32 px-2 py-1 bg-muted border border-border rounded text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                    // biome-ignore lint/a11y/noAutofocus: inline edit needs auto-focus for UX
                    autoFocus
                    disabled={isMutating}
                  />
                  <button
                    type="button"
                    onClick={handleProceedToConfirm}
                    disabled={isMutating || parsedValue === null}
                    className="px-3 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors disabled:opacity-50"
                  >
                    確認へ進む
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isMutating}
                    className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                </div>
                {validationError && (
                  <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {validationError}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-foreground">
                  {systemLimit ? formatCurrency(systemLimit.limitUSD) : '未設定'}
                </span>
                {stage === 'idle' && (
                  <>
                    <button
                      type="button"
                      onClick={handleEdit}
                      className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                      title="編集"
                      aria-label="編集"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {systemLimit && (
                      <button
                        type="button"
                        onClick={() => setStage('confirming-delete')}
                        disabled={isMutating}
                        className="p-1 text-muted-foreground hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                        title="削除"
                        aria-label="削除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* 保存の確認ステップ */}
          {stage === 'confirming-save' && parsedValue !== null && (
            <section
              aria-label="保存内容の確認"
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
            >
              <p className="text-sm text-foreground">
                この上限は<strong>全ユーザー</strong>の合計コストに対して一斉に適用されます。
              </p>
              <p className="text-sm text-muted-foreground">
                変更前: {systemLimit ? formatCurrency(systemLimit.limitUSD) : '未設定'} → 変更後:{' '}
                {formatCurrency(parsedValue)}
              </p>
              {parsedValue === 0 && (
                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                  上限を 0
                  にすると、全ユーザーの利用が即時停止します（実行中のエージェントループの中断を含む）。
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleConfirmSave}
                  disabled={isMutating}
                  className="px-3 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  {isMutating ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      保存中
                    </span>
                  ) : (
                    'この内容で保存'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isMutating}
                  className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  取消
                </button>
              </div>
            </section>
          )}

          {/* 削除の確認ステップ */}
          {stage === 'confirming-delete' && (
            <section
              aria-label="削除内容の確認"
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
            >
              <p className="text-sm text-foreground">
                システム全体の上限を削除すると、<strong>全ユーザー</strong>
                の合計コストに対する天井がなくなります。
              </p>
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isMutating}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isMutating ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      削除中
                    </span>
                  ) : (
                    'この内容で削除'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isMutating}
                  className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  取消
                </button>
              </div>
            </section>
          )}

          {/* 保存・削除の失敗理由 */}
          {errorMessage && (
            <p
              role="alert"
              className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400"
            >
              {errorMessage}
            </p>
          )}

          {/* 説明 */}
          <p className="text-xs text-muted-foreground">
            全ユーザーの合計コストがこの上限に達すると、利用者によらず以降の LLM
            リクエストが遮断されます。1 ユーザーあたりの上限とは独立に適用されます。
          </p>

          {/* 当月の消化状況 */}
          <div className="pt-2 border-t border-border space-y-1">
            {statusAvailable ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">当月の合計コスト</span>
                  <span className="text-foreground">
                    {formatCurrency(systemStatus.currentCost)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">使用率</span>
                  <span
                    className={
                      isExceeded
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : isNearLimit
                          ? 'text-amber-600 dark:text-amber-400 font-medium'
                          : 'text-foreground'
                    }
                  >
                    {usagePercent === null ? '—' : `${usagePercent}%`}
                  </span>
                </div>
                {/* 上限未設定（usagePercent = null）では分母がないため帯を出さない */}
                {usagePercent !== null && (
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={
                        isExceeded
                          ? 'h-full bg-destructive'
                          : isNearLimit
                            ? 'h-full bg-amber-400'
                            : 'h-full bg-blue-400'
                      }
                      // 頭打ちするのは帯の幅と支援技術へ渡す値だけ。数字は実際の使用率を出す
                      style={{ width: `${Math.min(100, usagePercent)}%` }}
                      role="progressbar"
                      aria-label="システム全体の月額コスト上限の使用率"
                      aria-valuenow={Math.min(100, usagePercent)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">対象期間</span>
                  <span className="text-foreground">
                    {formatPeriod(systemStatus.period)}（JST）
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                消化状況を取得できませんでした。システム全体上限が実質的に無効化されている可能性があります。
              </p>
            )}
          </div>

          {/* 逼迫・到達の表示 */}
          {isExceeded && (
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              全ユーザーのリクエストが制限中です。
            </p>
          )}
          {isNearLimit && (
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
              システム全体の利用が上限に近づいています。
            </p>
          )}

          {/* 当月固定であることの明示 */}
          {!isCurrentMonth && (
            <p className="text-xs text-muted-foreground">
              この消化状況は表示中の月ではなく当月（JST）・全モデルの合計です。ページの月送り・モデル絞り込みの影響を受けません。
            </p>
          )}

          {/* 更新情報 */}
          {systemLimit && (
            <div className="text-xs text-muted-foreground">
              最終更新: {new Date(systemLimit.updatedAt).toLocaleString('ja-JP')} (
              {systemLimit.updatedBy})
            </div>
          )}
        </div>
      )}
    </div>
  );
}
